import { useState, useEffect, FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import type { Profile, UserRole } from '../lib/types'
import {
  Users, UserPlus, Trash2, Shield, ShieldCheck, Eye, ShieldAlert, UserCheck,
  Loader2, AlertCircle, Search, X, Check, Edit3, Save
} from 'lucide-react'

interface NewUserForm {
  nome: string
  cognome: string
  email: string
  role: UserRole
}

const ROLE_LABELS: Record<UserRole, string> = {
  staff: 'Staff',
  staff_plus: 'Staff Plus',
  admin: 'Admin',
  admin_plus: 'Admin Plus',
  super_admin: 'Super Admin',
}

const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  staff: 'Solo visualizzazione archivio',
  staff_plus: 'Visualizzazione e modifica archivio',
  admin: 'Modifica archivio + visualizzazione colloqui',
  admin_plus: 'Modifica archivio e colloqui',
  super_admin: 'Controllo totale',
}

const ROLE_ICONS: Record<UserRole, typeof Shield> = {
  staff: Eye,
  staff_plus: UserCheck,
  admin: Shield,
  admin_plus: ShieldAlert,
  super_admin: ShieldCheck,
}

const ROLE_COLORS: Record<UserRole, string> = {
  staff: 'bg-slate-100 text-slate-600 border-slate-200',
  staff_plus: 'bg-blue-100 text-blue-700 border-blue-200',
  admin: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  admin_plus: 'bg-purple-100 text-purple-700 border-purple-200',
  super_admin: 'bg-amber-100 text-amber-700 border-amber-200',
}

const DEFAULT_PASSWORD = 'Gestione.infermieri.2026'

export function UserManager() {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [form, setForm] = useState<NewUserForm>({
    nome: '',
    cognome: '',
    email: '',
    role: 'staff',
  })

  useEffect(() => {
    fetchProfiles()
  }, [])

  async function fetchProfiles() {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching profiles:', error)
    } else {
      setProfiles(data as Profile[])
    }
    setIsLoading(false)
  }

  async function handleCreateUser(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')
    if (!form.nome.trim() || !form.cognome.trim()) {
      setError('Nome e cognome sono obbligatori')
      return
    }
    setSaving(true)

    try {
      const displayName = `${form.nome.trim()} ${form.cognome.trim()}`
      
      // Use user-provided email or auto-generate with a valid-looking domain
      let email = form.email.trim()
      if (!email) {
        const emailLocal = `${form.nome.trim().toLowerCase().replace(/\s+/g, '.')}.${form.cognome.trim().toLowerCase().replace(/\s+/g, '.')}`
        email = `${emailLocal}@quicare.app`
      }

      // 1. Create user via Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password: DEFAULT_PASSWORD,
        options: {
          data: {
            display_name: displayName,
            role: form.role,
          },
          emailRedirectTo: undefined,
        },
      })

      if (authError) throw new Error(authError.message)
      if (!authData.user) throw new Error('Utente non creato')

      // 2. Insert or update profile with email
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          display_name: displayName,
          email: email,
          role: form.role,
        })

      if (profileError) throw new Error(profileError.message)

      setSuccess(`✅ Utente "${displayName}" creato!\nEmail: ${email}\nPassword: ${DEFAULT_PASSWORD}`)
      setForm({ nome: '', cognome: '', email: '', role: 'staff' })
      setShowForm(false)
      fetchProfiles()
    } catch (err: any) {
      setError(err.message || 'Errore durante la creazione')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdateRole(profileId: string, newRole: UserRole) {
    const { error } = await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', profileId)

    if (error) {
      setError('Errore aggiornamento ruolo: ' + error.message)
    } else {
      setSuccess('Ruolo aggiornato!')
      fetchProfiles()
    }
  }

  async function handleUpdateName(profileId: string) {
    if (!editName.trim()) return
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: editName.trim() })
      .eq('id', profileId)

    if (error) {
      setError('Errore aggiornamento nome: ' + error.message)
    } else {
      setSuccess('Nome aggiornato!')
      setEditingId(null)
      setEditName('')
      fetchProfiles()
    }
  }

  async function handleDeleteUser(profileId: string, displayName: string) {
    if (!confirm(`Sei sicuro di voler eliminare l'utente "${displayName}"?`)) return

    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', profileId)

    if (error) {
      setError('Errore eliminazione: ' + error.message)
    } else {
      setSuccess('Utente eliminato')
      fetchProfiles()
    }
  }

  const filteredProfiles = profiles.filter(p =>
    p.display_name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl shadow-lg shadow-pink-500/20">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800">Gestione Utenti</h2>
              <p className="text-sm text-slate-400">{profiles.length} utenti registrati</p>
            </div>
          </div>

          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:from-pink-400 hover:to-rose-500 transition-all text-sm"
          >
            {showForm ? <X className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            {showForm ? 'Annulla' : 'Nuovo Utente'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm animate-fade-in">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-2 text-green-700 text-sm animate-fade-in whitespace-pre-line">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* New user form */}
      {showForm && (
        <form onSubmit={handleCreateUser} className="bg-white rounded-2xl shadow-sm border border-pink-100 p-6 animate-fade-in">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-pink-500" />
            Crea Nuovo Utente
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Nome</label>
              <input
                type="text"
                value={form.nome}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                required
                placeholder="Elena"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Cognome</label>
              <input
                type="text"
                value={form.cognome}
                onChange={e => setForm({ ...form, cognome: e.target.value })}
                required
                placeholder="Colacino"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Email <span className="text-slate-300 font-normal">(opzionale)</span></label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="elena@email.com"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1 uppercase tracking-wider">Ruolo</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value as UserRole })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white"
              >
                <option value="staff">Staff — Solo visualizzazione</option>
                <option value="staff_plus">Staff Plus — Visualizzazione e modifica</option>
                <option value="admin">Admin — Archivio + lettura colloqui</option>
                <option value="admin_plus">Admin Plus — Archivio + colloqui</option>
                <option value="super_admin">Super Admin — Controllo totale</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-slate-400 italic">
              🔑 Password iniziale: <span className="font-bold text-slate-600">{DEFAULT_PASSWORD}</span>
              {!form.email && <span className="block mt-1 text-slate-300">Se non inserisci un'email, verrà generata automaticamente.</span>}
            </p>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all text-sm disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              {saving ? 'Creazione...' : 'Crea Utente'}
            </button>
          </div>
        </form>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
        <input
          placeholder="Cerca utenti..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
        />
      </div>

      {/* Users list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Caricamento utenti...
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredProfiles.map(profile => {
            const RoleIcon = ROLE_ICONS[profile.role] || Eye
            const isEditing = editingId === profile.id
            return (
              <div
                key={profile.id}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-xl flex items-center justify-center text-white font-black text-sm shadow-md flex-shrink-0">
                      {profile.display_name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleUpdateName(profile.id); if (e.key === 'Escape') { setEditingId(null); setEditName('') } }}
                            autoFocus
                            className="w-full px-2 py-1 border border-pink-300 rounded-lg text-sm font-bold focus:outline-none focus:ring-2 focus:ring-pink-500"
                          />
                          <button onClick={() => handleUpdateName(profile.id)} className="p-1 hover:bg-green-50 rounded-lg" title="Salva"><Save className="w-3.5 h-3.5 text-green-600" /></button>
                          <button onClick={() => { setEditingId(null); setEditName('') }} className="p-1 hover:bg-slate-50 rounded-lg" title="Annulla"><X className="w-3.5 h-3.5 text-slate-400" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-bold text-slate-800 text-sm truncate">{profile.display_name}</h3>
                          <button
                            onClick={() => { setEditingId(profile.id); setEditName(profile.display_name) }}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-indigo-50 rounded-lg transition-all flex-shrink-0"
                            title="Modifica nome"
                          >
                            <Edit3 className="w-3 h-3 text-indigo-500" />
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-slate-400">{ROLE_DESCRIPTIONS[profile.role]}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteUser(profile.id, profile.display_name)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                    title="Elimina utente"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border ${ROLE_COLORS[profile.role]}`}>
                    <RoleIcon className="w-3.5 h-3.5" />
                    {ROLE_LABELS[profile.role]}
                  </span>
                  <select
                    value={profile.role}
                    onChange={e => handleUpdateRole(profile.id, e.target.value as UserRole)}
                    className="ml-auto text-xs px-2 py-1 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-pink-400"
                    title="Cambia ruolo"
                  >
                    <option value="staff">Staff</option>
                    <option value="staff_plus">Staff Plus</option>
                    <option value="admin">Admin</option>
                    <option value="admin_plus">Admin Plus</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!isLoading && filteredProfiles.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">
          {search ? 'Nessun utente trovato' : 'Nessun utente registrato'}
        </div>
      )}
    </div>
  )
}
